/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import React, { useState } from 'react';
import axios from 'axios';
import './ShareDialog.css';

// Use same API URL pattern as api.js
const API_URL = process.env.REACT_APP_API_URL || 'https://skilldelivery-platform.onrender.com/api';

const ShareDialog = ({ isOpen, onClose, conversationId, messageCount }) => {
  const [shareUrl, setShareUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [shareSettings, setShareSettings] = useState({
    expiresIn: '7d', // 7 days default
    includeTimestamps: true,
    allowDownload: true
  });

  if (!isOpen) return null;

  const generateShareLink = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/skillbot/share`,
        {
          conversationId,
          settings: shareSettings
        },
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      const shareUrl = `${window.location.origin}/share/${response.data.shareId}`;
      setShareUrl(shareUrl);
    } catch (error) {
      console.error('Error generating share link:', error);
      setError(error.response?.data?.error || 'Failed to generate share link');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    });
  };

  const copyAsMarkdown = () => {
    const markdown = `[SkillBot Conversation](${shareUrl})`;
    navigator.clipboard.writeText(markdown).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    });
  };

  const shareViaEmail = () => {
    const subject = encodeURIComponent('Check out this SkillBot conversation');
    const body = encodeURIComponent(`I wanted to share this conversation with you:\n\n${shareUrl}\n\nShared via SkillDelivery`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const shareViaWhatsApp = () => {
    const text = encodeURIComponent(`Check out this SkillBot conversation: ${shareUrl}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const shareViaTwitter = () => {
    const text = encodeURIComponent(`Just had an insightful conversation with SkillBot!`);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(shareUrl)}`, '_blank');
  };

  const shareViaLinkedIn = () => {
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank');
  };

  return (
    <div className="share-dialog-overlay" onClick={onClose}>
      <div className="share-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>📤 Share Conversation</h2>
          <button onClick={onClose} className="close-button">×</button>
        </div>

        <div className="dialog-content">
          {!shareUrl ? (
            <>
              <div className="conversation-info">
                <p className="info-text">
                  Share this conversation ({messageCount} messages) with others.
                  They'll be able to view all messages with timestamps in their timezone.
                </p>
              </div>

              <div className="share-settings">
                <h3>Share Settings</h3>
                
                <div className="setting-group">
                  <label htmlFor="expiresIn">Link Expiration</label>
                  <select
                    id="expiresIn"
                    value={shareSettings.expiresIn}
                    onChange={(e) => setShareSettings({
                      ...shareSettings,
                      expiresIn: e.target.value
                    })}
                  >
                    <option value="1h">1 Hour</option>
                    <option value="24h">24 Hours</option>
                    <option value="7d">7 Days</option>
                    <option value="30d">30 Days</option>
                    <option value="never">Never Expires</option>
                  </select>
                </div>

                <div className="setting-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={shareSettings.includeTimestamps}
                      onChange={(e) => setShareSettings({
                        ...shareSettings,
                        includeTimestamps: e.target.checked
                      })}
                    />
                    <span>Include timestamps on shared view</span>
                  </label>
                </div>

                <div className="setting-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={shareSettings.allowDownload}
                      onChange={(e) => setShareSettings({
                        ...shareSettings,
                        allowDownload: e.target.checked
                      })}
                    />
                    <span>Allow viewers to download conversation</span>
                  </label>
                </div>
              </div>

              {error && (
                <div className="error-message">
                  ⚠️ {error}
                </div>
              )}

              <button
                onClick={generateShareLink}
                disabled={loading}
                className="generate-button"
              >
                {loading ? '⏳ Generating Link...' : '🔗 Generate Share Link'}
              </button>
            </>
          ) : (
            <>
              <div className="share-success">
                <div className="success-icon">✓</div>
                <p>Your share link is ready!</p>
              </div>

              <div className="share-url-container">
                <input
                  type="text"
                  value={shareUrl}
                  readOnly
                  className="share-url-input"
                />
                <button onClick={copyToClipboard} className="copy-url-button">
                  {copied ? '✓ Copied!' : '📋 Copy'}
                </button>
              </div>

              <div className="share-options">
                <h3>Share via:</h3>
                <div className="share-buttons">
                  <button onClick={shareViaEmail} className="share-option-btn email">
                    <span className="icon">📧</span>
                    <span>Email</span>
                  </button>
                  <button onClick={shareViaWhatsApp} className="share-option-btn whatsapp">
                    <span className="icon">💬</span>
                    <span>WhatsApp</span>
                  </button>
                  <button onClick={shareViaTwitter} className="share-option-btn twitter">
                    <span className="icon">🐦</span>
                    <span>Twitter</span>
                  </button>
                  <button onClick={shareViaLinkedIn} className="share-option-btn linkedin">
                    <span className="icon">💼</span>
                    <span>LinkedIn</span>
                  </button>
                </div>
              </div>

              <div className="additional-actions">
                <button onClick={copyAsMarkdown} className="secondary-button">
                  Copy as Markdown
                </button>
              </div>

              <div className="share-info">
                <p className="info-note">
                  🔒 This link {shareSettings.expiresIn === 'never' 
                    ? 'will never expire' 
                    : `will expire in ${shareSettings.expiresIn.replace('h', ' hour(s)').replace('d', ' day(s)')}`
                  }
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareDialog;
